import { categoryLabel } from "../copilot/copilotText";

const minutes = (value, t) => {
  if (value === null || value === undefined) return t.analytics.noData;
  if (value < 60) return t.analytics.minutes(value);
  const h = Math.floor(value / 60);
  const m = value % 60;
  return m ? t.analytics.hoursMinutes(h, m) : t.analytics.hours(h);
};

const Bar = ({ value, max }) => (
  <span className="ca-bar" aria-hidden="true">
    <span style={{ inlineSize: `${max ? Math.max(4, Math.round((value / max) * 100)) : 0}%` }} />
  </span>
);

// Support analytics from the statistics endpoint (cheap, cached on the server, scoped to what the
// viewer may see). Everything shown here is a count or an average over the last 30 days.
export default function AnalyticsPanel({ inbox, lang }) {
  const { t, stats, categories } = inbox;
  if (!stats) return <div className="ca-analytics"><p className="ca-muted" role="status">{t.loading}</p></div>;

  const cats = Object.entries(stats.byCategory || {}).sort((a, b) => b[1] - a[1]);
  const maxCat = cats[0]?.[1] || 0;
  const agents = stats.unresolvedByAgent || [];
  const maxAgent = agents[0]?.count || 0;
  const perDay = stats.createdPerDay || [];
  const maxDay = Math.max(0, ...perDay.map((d) => d.count));

  return (
    <section className="ca-analytics" id="ca-analytics" aria-label={t.analytics.title}>
      <div className="ca-an-card">
        <h4>{t.analytics.responseTimes}</h4>
        <dl>
          <div><dt>{t.analytics.firstResponse}</dt><dd>{minutes(stats.averageFirstResponseMinutes, t)}</dd></div>
          <div><dt>{t.analytics.resolution}</dt><dd>{minutes(stats.averageResolutionMinutes, t)}</dd></div>
          <div><dt>{t.analytics.last7}</dt><dd>{t.analytics.createdResolved(stats.last7Days?.created ?? 0, stats.last7Days?.resolved ?? 0)}</dd></div>
        </dl>
        <p className="ca-hint">{t.analytics.window}</p>
      </div>

      <div className="ca-an-card">
        <h4>{t.analytics.openPending}</h4>
        <ul className="ca-an-list">
          <li><span>{t.statusNames.open}</span><Bar value={stats.byStatus.open} max={Math.max(stats.byStatus.open, stats.byStatus.pending)} /><strong>{stats.byStatus.open}</strong></li>
          <li><span>{t.statusNames.pending}</span><Bar value={stats.byStatus.pending} max={Math.max(stats.byStatus.open, stats.byStatus.pending)} /><strong>{stats.byStatus.pending}</strong></li>
          <li><span>{t.stats.unassigned}</span><Bar value={stats.unassigned} max={Math.max(stats.unresolved, 1)} /><strong>{stats.unassigned}</strong></li>
        </ul>
      </div>

      <div className="ca-an-card">
        <h4>{t.analytics.byCategory}</h4>
        {cats.length === 0 ? <p className="ca-muted">{t.analytics.noData}</p> : (
          <ul className="ca-an-list">
            {cats.map(([key, count]) => (
              <li key={key}><span>{categoryLabel(categories, key, lang)}</span><Bar value={count} max={maxCat} /><strong>{count}</strong></li>
            ))}
          </ul>
        )}
      </div>

      <div className="ca-an-card">
        <h4>{t.analytics.byAgent}</h4>
        {agents.length === 0 ? <p className="ca-muted">{t.analytics.noData}</p> : (
          <ul className="ca-an-list">
            {agents.map((agent) => (
              <li key={agent.agentId}>
                <span title={t.analytics.agentSplit(agent.open, agent.pending)}>{agent.name || t.assignedAgentBadge}</span>
                <Bar value={agent.count} max={maxAgent} />
                <strong>{agent.count}</strong>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="ca-an-card ca-an-wide">
        <h4>{t.analytics.perDay}</h4>
        {perDay.length === 0 ? <p className="ca-muted">{t.analytics.noData}</p> : (
          <div className="ca-days" role="img" aria-label={t.analytics.perDay}>
            {perDay.map((d) => (
              <span key={d.date} className="ca-day" title={`${d.date}: ${d.count}`}>
                <span style={{ blockSize: `${maxDay ? Math.max(6, Math.round((d.count / maxDay) * 100)) : 0}%` }} />
              </span>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
